import abis from './abis.json'
import { zeroAddress, type Abi, type Address } from 'viem'
import { contractsChain } from '@/lib/wagmi'

/**
 * Deployed contract addresses and ABIs.
 *
 * The ABIs are generated from the compiled artifacts and trimmed to the functions
 * the UI actually calls — a full ABI is mostly dead weight in the bundle, and a
 * trimmed one makes it obvious at a glance what surface the frontend touches.
 *
 * Regenerate with `npm run abis` in contracts-workspace after any contract change,
 * or the UI will encode calls against a signature that no longer exists and fail
 * with an opaque revert.
 */

// JSON imports widen `type: "function"` to `string`, which no longer satisfies
// viem's `Abi`. The cast is the standard fix; the shape is generated, not hand-written.
export const bondMarketAbi = abis.bondMarket as Abi
export const bondCertificateAbi = abis.bondCertificate as Abi
export const cdxAbi = abis.cdx as Abi
export const pairAbi = abis.pair as Abi
export const loopVaultAbi = abis.loopVault as Abi
export const loopRouterAbi = abis.loopRouter as Abi
export const floorOracleAbi = abis.floorOracle as Abi
export const genesisBondingCurveAbi = abis.genesisBondingCurve as Abi
export const routerAbi = abis.router as Abi
export const erc20Abi = abis.erc20 as Abi

const env = import.meta.env

const configuredAddress = (
  name: keyof ImportMetaEnv,
  testnetFallback: Address,
  requiredOnMainnet = false,
): Address => {
  const value = env[name]?.trim()
  if (value) return value as Address
  if (contractsChain.id === 56 && requiredOnMainnet) {
    throw new Error(`${name} is required for a BSC mainnet build`)
  }
  // Never fall back to a testnet deployment while connected to mainnet. Features
  // whose contracts have not shipped yet receive the zero address and fail only
  // when visited, rather than preventing the Genesis-only production app from booting.
  if (contractsChain.id === 56) return zeroAddress
  return testnetFallback
}

/**
 * Addresses come from env so a redeploy does not need a code change. CrystalDEX
 * contracts fall back to the zero address until they are deployed and set in env;
 * only the public PancakeSwap factory and router carry real testnet defaults.
 */
export const CONTRACTS = {
  bondMarket: configuredAddress('VITE_BOND_MARKET_ADDRESS', '0x0000000000000000000000000000000000000000'),
  bondCertificate: configuredAddress('VITE_BOND_CERTIFICATE_ADDRESS', '0x0000000000000000000000000000000000000000'),
  genesisBondCertificate: configuredAddress('VITE_GENESIS_BOND_CERTIFICATE_ADDRESS', zeroAddress, true),
  cdx: configuredAddress('VITE_CDX_ADDRESS', '0x0000000000000000000000000000000000000000'),
  usdt: configuredAddress('VITE_USDT_ADDRESS', '0x0000000000000000000000000000000000000000', true),
  genesisBondingCurve: configuredAddress('VITE_GENESIS_BONDING_CURVE_ADDRESS', '0x0000000000000000000000000000000000000000', true),

  /*
   * PancakeSwap V2 on BNB testnet — the real deployment, not a stand-in. These are
   * the same contracts, at the same interface, that BSC mainnet uses; going to
   * production changes these two addresses and nothing else.
   */
  factory: configuredAddress('VITE_DEX_FACTORY_ADDRESS', '0x6725F303b657a9451d8BA641348b6761A6CC7a17'),
  router: configuredAddress('VITE_DEX_ROUTER_ADDRESS', '0xD99D1c33F9fC3444f8101754aBC46c52416550D1'),
  /** The CDX/USDT pair. Its reserves ARE the price; its Sync events are the chart. */
  pair: configuredAddress('VITE_CDX_PAIR_ADDRESS', '0x0000000000000000000000000000000000000000'),

  /*
   * Looping. Collateral is priced by `loopOracle` at the treasury floor rather than
   * the pool, because the pool is shallow enough to move ~60% for 50k USDT — see
   * docs/LOOPING-PLAN.md.
   */
  loopOracle: configuredAddress('VITE_LOOP_ORACLE_ADDRESS', '0x0000000000000000000000000000000000000000'),
  loopVault: configuredAddress('VITE_LOOP_VAULT_ADDRESS', '0x0000000000000000000000000000000000000000'),
  loopRouter: configuredAddress('VITE_LOOP_ROUTER_ADDRESS', '0x0000000000000000000000000000000000000000'),
} as const

/**
 * Where the contracts above are deployed. Taken from the wagmi chain object rather
 * than from env: wagmi only has a transport for the chains in its own list, so an
 * env value pointing anywhere else would produce reads that can never succeed.
 * Moving chains is a code change, by design.
 */
export const CONTRACTS_CHAIN_ID = contractsChain.id
export const NETWORK_NAME = CONTRACTS_CHAIN_ID === 56 ? 'BSC Mainnet' : 'BSC Testnet'

/**
 * Explorer links, derived from the chain rather than hardcoded.
 *
 * These were pinned to amoy.polygonscan.com. When the contracts moved to BNB
 * testnet every "view on explorer" link kept pointing at Polygon, where the
 * transaction does not exist — a broken link that still looks authoritative.
 */
const EXPLORER_BASE =
  contractsChain.blockExplorers?.default.url ?? 'https://bscscan.com'

export const EXPLORER_TX = (hash: string) => `${EXPLORER_BASE}/tx/${hash}`
export const EXPLORER_ADDRESS = (addr: string) => `${EXPLORER_BASE}/address/${addr}`
export const EXPLORER_NFT = (addr: string, tokenId: string) => `${EXPLORER_BASE}/nft/${addr}/${tokenId}`
export const EXPLORER_NAME = contractsChain.blockExplorers?.default.name ?? 'the explorer'

/** PancakeSwap's own swap UI, pre-filled with the CDX pair. */
export const PANCAKE_SWAP_URL = (input: string, output: string) =>
  `https://pancakeswap.finance/swap?chain=${contractsChain.id === 56 ? 'bsc' : 'bscTestnet'}&inputCurrency=${input}&outputCurrency=${output}`
export const PANCAKE_INFO_URL = (pair: string) =>
  `https://pancakeswap.finance/info/pairs/${pair}`

/** Matches BondMarket.RATE_PRECISION. */
export const RATE_PRECISION = 10n ** 18n

export interface Offering {
  id: bigint
  paymentToken: Address
  payoutToken: Address
  lockDuration: bigint
  payoutRate: bigint
  active: boolean
  pairLabel: string
}

/**
 * The only lock term currently offered for NEW purchases: 360 days, in seconds.
 *
 * ── This hides, it does not enforce ──
 * The 90 and 180 day offerings are still active on the BondMarket contract, so a direct
 * `purchaseBond` call can still buy one. Enforcement requires the owner to deactivate
 * them on chain. Saying so here is the point: a reader who assumes this is a guarantee
 * would be wrong.
 *
 * ── Matched on duration, not id ──
 * Offering ids are assigned in creation order, so a replacement 360-day offering would
 * carry a different one and an id list would hide the very term it exists to show.
 */
export const OFFERED_LOCK_SECONDS = 360n * 86_400n

/**
 * Whether an offering may be shown for a NEW purchase.
 *
 * Use ONLY where offerings are listed to buy. Looking up the offering behind a bond a
 * user already holds must not go through this — a wallet holding a 90-day bond still
 * needs its term and rate rendered, and filtering the lookup makes those positions show
 * up blank.
 */
export function isPurchasableOffering(offering: Offering): boolean {
  return offering.active
}

export interface UserBond {
  id: bigint
  offeringId: bigint
  owner: Address
  payoutAmount: bigint
  paymentPaid: bigint
  startTime: bigint
  lastClaimTime: bigint
  claimedPrincipal: bigint
  claimedRewards: bigint
  fullyClaimed: boolean
}

/**
 * Vesting is linear from `startTime` over `lockDuration` — the same formula the
 * contract uses in `_claimablePrincipal`. Computed client-side so progress bars
 * and countdowns tick smoothly between blocks instead of only moving when a
 * transaction lands. The contract remains authoritative for actual payouts.
 */
export function vestingProgress(bond: UserBond, lockDuration: bigint, nowSeconds: number) {
  const elapsed = BigInt(Math.max(0, nowSeconds - Number(bond.startTime)))
  const capped = elapsed > lockDuration ? lockDuration : elapsed
  const vested =
    lockDuration === 0n ? bond.payoutAmount : (bond.payoutAmount * capped) / lockDuration
  const claimable = vested > bond.claimedPrincipal ? vested - bond.claimedPrincipal : 0n

  return {
    vested,
    claimable,
    /** 0..1 */
    fraction: lockDuration === 0n ? 1 : Number(capped) / Number(lockDuration),
    endsAt: Number(bond.startTime) + Number(lockDuration),
    matured: elapsed >= lockDuration,
  }
}

/** Human label for a lock duration, e.g. 7776000n -> "90 days". */
export function lockLabel(seconds: bigint): string {
  const days = Number(seconds) / 86_400
  if (days >= 1) return `${Math.round(days)} day${Math.round(days) === 1 ? '' : 's'}`
  const hours = Number(seconds) / 3_600
  return `${Math.round(hours)}h`
}

/** Bond premium as a percentage, e.g. rate 1.05e18 -> "+5.0%". */
export function ratePremium(payoutRate: bigint): string {
  const pct = (Number(payoutRate) / Number(RATE_PRECISION) - 1) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
}

/**
 * The bond premium expressed as an annualised rate.
 *
 * ── Why this is not called APY ──
 * A bond's payout is FIXED at purchase and vests linearly. There is no compounding,
 * no variable rate and no reinvestment — so "APY", which implies a yield that
 * compounds and can change, would describe a different product. What this returns is
 * the equivalent annual rate of a one-off premium earned over the term:
 *
 *     (1 + premium) ^ (365 / termDays) − 1
 *
 * A 90-day bond at +5% annualises to ~21.6% because the premium is earned four times
 * a year IF you keep re-bonding at the same rate — which is a real assumption a reader
 * has to make, and the reason this figure is labelled "equivalent annual" rather than
 * presented as something you are guaranteed.
 */
export function annualisedRate(payoutRate: bigint, lockSeconds: bigint): number {
  const premium = Number(payoutRate) / Number(RATE_PRECISION) - 1
  const termDays = Number(lockSeconds) / 86_400
  if (termDays <= 0 || premium <= -1) return 0
  return (1 + premium) ** (365 / termDays) - 1
}

/** How much payout accrues per second, in display units. Drives the streaming figures. */
export function accrualPerSecond(payoutAmount: bigint, lockSeconds: bigint, decimals: number): number {
  const seconds = Number(lockSeconds)
  if (seconds <= 0) return 0
  return Number(payoutAmount) / 10 ** decimals / seconds
}
